/**
 * Module catalog — the single source of truth for per-cell "modules".
 *
 * A cell's value is produced by a *module*: `{ kind, ...config }`, where every
 * `kind` is described once here. Behaviour (resolve / default config / which
 * template strings it exposes for dependency analysis) and identity (name,
 * tooltip, category, tags, icon, tone) both live in one descriptor, so adding a
 * capability later (e.g. the Conditional module) is a matter of registering one
 * more entry — not editing the mapper switch, the dependency walker, and the
 * inspector kind-list in three different files.
 *
 * Phase A (this file's introduction) is intentionally INERT: it reproduces the
 * exact behaviour that previously lived inline in `mapping.ts`,
 * `dependencies.ts`, and `MapperInspector.tsx`. Those modules now delegate here.
 *
 * See docs/plan/13-modular-cell-engine.md.
 */

import { evalCondition } from "../conditions";
import type {
  Rule,
  Templatable,
} from "../types";
import {
  aiHandlePicker,
  aiModule,
  anthropicPicker,
  jevPicker,
  openaiPicker,
  openrouterPicker,
} from "@/modules/ai/module.server";
import { computedModule } from "@/modules/computed/module.server";
import { expressionModule } from "@/modules/expression/module.server";
import { findReplaceModule } from "@/modules/find-replace/module.server";
import { dictionaryModule } from "@/modules/dictionary/module.server";
import { dropboxImageModule } from "@/modules/dropbox-image/module.server";
import { priceEngineModule } from "@/modules/price-engine/module.server";
import { javascriptModule } from "@/modules/javascript/module.server";
import { sourceModule } from "@/modules/source/module.server";
import { staticModule } from "@/modules/static/module.server";
import { templateModule } from "@/modules/template/module.server";

// ---------------------------------------------------------------------------
// Shared engine primitives + contract types now live under src/modules/_shared.
// Re-exported here so existing importers of catalog.ts keep working unchanged.
// ---------------------------------------------------------------------------
import { computedFns, AI_RULE_PLACEHOLDER } from "@/modules/_shared/engine";
import type { ResolveCtx } from "@/modules/_shared/engine";
import type {
  CellModule,
  ModuleCategory,
  ModuleIcon,
  ModuleMeta,
} from "@/modules/_shared/contract";

export { computedFns, AI_RULE_PLACEHOLDER };
export type { ResolveCtx, CellModule, ModuleCategory, ModuleIcon, ModuleMeta };

// ---------------------------------------------------------------------------
// Cell modules — order here defines picker order (source → static → template →
// computed → conditional → ai), matching the picker flow.
// ---------------------------------------------------------------------------

const conditionalModule: CellModule = {
  kind: "conditional",
  capability: "cell",
  name: "If / Then Rules",
  tooltip: "Pick the value based on a condition",
  description:
    "Chooses what goes in this column based on rules you set, like a different value when the colour is red.",
  category: "Rules",
  tags: ["if", "else", "branch", "condition", "routing", "switch"],
  icon: { kind: "lucide", name: "GitBranch" },
  tone: "amber",
  defaultConfig: () => ({
    kind: "conditional",
    branches: [
      {
        when: { all: [{ column: "", op: "equals", value: "" }] },
        then: { kind: "static", value: "" },
      },
    ],
  }),
  resolve: (rule, ctx) => {
    if (rule.kind !== "conditional") return "";
    for (const branch of rule.branches) {
      if (!evalCondition(branch.when, ctx.row, ctx.output)) continue;
      // Nested "ai" modules inside a branch are NOT executed by the AI pass in
      // this phase; resolve returns the AI placeholder. The Phase C UI will
      // restrict branch modules to non-AI.
      return cellModule(branch.then.kind)?.resolve(branch.then, ctx) ?? "";
    }
    if (!rule.else) return "";
    return cellModule(rule.else.kind)?.resolve(rule.else, ctx) ?? "";
  },
  templateStrings: (rule) => {
    if (rule.kind !== "conditional") return [];
    const values: Templatable[] = [];
    for (const branch of rule.branches) {
      const predicates = "all" in branch.when ? branch.when.all : branch.when.any;
      for (const predicate of predicates) {
        const column = predicate.column.trim();
        if (column.toLowerCase().startsWith("out:")) values.push(`{{${column}}}`);
        // The predicate's comparison value may itself reference fields.
        if (predicate.value != null) values.push(predicate.value);
      }
      values.push(...templateStrings(branch.then));
    }
    if (rule.else) values.push(...templateStrings(rule.else));
    return values;
  },
};

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

const CELL_MODULES: readonly CellModule[] = [
  sourceModule,
  staticModule,
  templateModule,
  computedModule,
  dictionaryModule,
  findReplaceModule,
  expressionModule,
  priceEngineModule,
  javascriptModule,
  conditionalModule,
  aiModule,
  anthropicPicker,
  openaiPicker,
  openrouterPicker,
  jevPicker,
  aiHandlePicker,
  dropboxImageModule,
];

// Resolution map: key by kind, but presets must NOT clobber the canonical.
const CELL_BY_KIND: Record<string, CellModule> = {};
for (const m of CELL_MODULES) {
  if (m.pickerOnly) continue;
  CELL_BY_KIND[m.kind] = m;
}

/** All cell modules in picker order. */
export function listCellModules(): readonly CellModule[] {
  return CELL_MODULES;
}

/** Cell modules visible in the picker (excludes `hidden`). */
export function pickableCellModules(): CellModule[] {
  return CELL_MODULES.filter((m) => !m.hidden);
}

/** Look up a cell module by kind. */
export function cellModule(kind: string): CellModule | undefined {
  return CELL_BY_KIND[kind];
}

/** Look up a module by picker id (falls back to kind). For seeding from the picker. */
export function pickById(id: string): CellModule | undefined {
  return CELL_MODULES.find((m) => (m.id ?? m.kind) === id) ?? CELL_BY_KIND[id];
}

/** Whether a computed `fn` name is known (used for the unknown-fn warning). */
export function isKnownComputedFn(fn: string): boolean {
  return fn in computedFns;
}

/** Templated values of a rule, dispatched through its module. */
export function templateStrings(rule: Rule): Templatable[] {
  return cellModule(rule.kind)?.templateStrings(rule) ?? [];
}
